import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, CircularProgress, Typography, Button } from "@mui/material";
import { CheckCircleOutline, ErrorOutline } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { isMobile } from "react-device-detect";
import LandingLayout from "../landing/LandingLayout";

const API_BASE = process.env.REACT_APP_API_BASE;

const getQueryParams = (search) => {
  const params = new URLSearchParams(search);
  return {
    id: params.get("id"),
    success: params.get("success"),
    amountCents: params.get("amount_cents"),
    currency: params.get("currency"),
    message: params.get("data.message")
  };
};

const TransactionStatus = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isValid, setIsValid] = useState(null);
  const [error, setError] = useState(null);

  const { id, success, amountCents, currency, message } = getQueryParams(location.search);

  useEffect(() => {
    const controller = new AbortController();

    const checkPayment = async () => {
      if (!id) {
        setError("Missing transaction ID.");
        setLoading(false);
        return;
      }

      try {
        const resp = await fetch(`${API_BASE}/callbacks/check-payment?transactionId=${encodeURIComponent(id)}`, {
          method: "GET",
          signal: controller.signal
        });

        if (!resp.ok) {
          const data = await resp.json().catch(() => ({}));
          throw new Error(data?.message || "Unable to verify transaction.");
        }

        const data = await resp.json();
        setIsValid(Boolean(data?.isValid));
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(err.message || "Something went wrong while checking the transaction.");
        }
      } finally {
        setLoading(false);
      }
    };

    checkPayment();

    return () => {
      controller.abort();
    };
  }, [id]);

  const derivedSuccess =
    isValid !== null
      ? isValid
      : success === "true";

  const title = derivedSuccess ? "Transaction Processed" : "Transaction Failed";
  const description =
    derivedSuccess
      ? message || "Your payment has been successfully processed."
      : error || message || "We were unable to process your payment. Please try again or contact support.";

  const amountText =
    amountCents && currency
      ? `${(Number(amountCents) / 100).toFixed(2)} ${currency}`
      : null;

  return (
    <LandingLayout>
    <Box
      minHeight="60vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      paddingX={isMobile ? 2 : 4}
      paddingY={isMobile ? 4 : 8}
    >
      <Card
        className="chainz-status-card"
        elevation={0}
        sx={{
          maxWidth: 480,
          width: "100%",
          borderRadius: 3,
          textAlign: "center",
          paddingY: isMobile ? 4 : 6,
          paddingX: isMobile ? 2 : 4,
          background: "#fff !important",
          border: "1px solid rgba(26,20,22,0.08) !important",
          color: "#1a1416 !important",
        }}
      >
        <CardContent>
          {loading ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={2}>
              <CircularProgress sx={{ color: "#7D010C" }} />
              <Typography variant={isMobile ? "body1" : "h6"} sx={{ color: "#1a1416" }}>
                Checking your transaction status...
              </Typography>
            </Box>
          ) : (
            <>
              {derivedSuccess ? (
                <CheckCircleOutline
                  sx={{
                    fontSize: isMobile ? 56 : 72,
                    color: "success.main",
                    mb: 2
                  }}
                />
              ) : (
                <ErrorOutline
                  sx={{
                    fontSize: isMobile ? 56 : 72,
                    color: "error.main",
                    mb: 2
                  }}
                />
              )}

              <Typography
                variant={isMobile ? "h5" : "h4"}
                fontWeight={600}
                gutterBottom
                sx={{ color: "#1a1416" }}
              >
                {title}
              </Typography>

              <Typography
                variant="body1"
                sx={{ color: "#6b6470 !important" }}
                paragraph
              >
                {description}
              </Typography>

              {amountText && (
                <Typography
                  variant="subtitle1"
                  fontWeight={500}
                  gutterBottom
                >
                  Amount: {amountText}
                </Typography>
              )}

              {id && (
                <Typography variant="caption" sx={{ color: "#6b6470 !important" }} display="block" gutterBottom>
                  Transaction ID: {id}
                </Typography>
              )}

              <Box mt={4} display="flex" justifyContent="center">
                <Button
                  variant="contained"
                  onClick={() => navigate("/")}
                  sx={{
                    minWidth: 160,
                    background: "linear-gradient(160deg, #a3121d, #6b0009)",
                    color: "#fff",
                    borderRadius: 999,
                    "&:hover": { background: "linear-gradient(160deg, #c0303e, #7a010a)" },
                  }}
                >
                  Back to Home
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
    </LandingLayout>
  );
};

export default TransactionStatus;

